import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { NavigateFunction, BrowserRouter as Router, useNavigate } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { AppState, Auth0Provider } from "@auth0/auth0-react";
import { LatLngBoundsExpression } from "leaflet";

import App from "./App";
import store from "./redux/store";
import i18n from "./i18n";
import ReactErrorBoundary from "./components/layout/ReactErrorBoundary";
import { getDomainAndClientId } from "./utils/utilsAuth0";
import useAgencyData from "./hooks/useAgencyData";
import TailSpinner from "./components/layout/TailSpinner";
import { AgencyProvider } from "./hooks/AgencyContext";
import { configAgencyName } from "./config";
import ApiErrorPage from "./components/layout/ApiErrorPage";

//* Testing staff in dev only!
if (process.env.NODE_ENV === "development") {
  import("./lib/tempTesting")
    .then((_module) => {
      // console.log("_module:", _module);
    })
    .catch((err) => {
      console.error("Error loading tempTesting", err);
    });
}

const MainAppContent = ({
  url,
  agencyName,
  agencyId,
  initialMapCenter,
  initialMapRange,
  domain,
  clientId,
  lang,
  timeZone,
  timeZoneOffsetMinutes,
  speedUnit,
}: {
  url: string;
  agencyName: string;
  agencyId: string;
  initialMapCenter: LanLngPoint;
  initialMapRange: LatLngBoundsExpression;
  domain: string;
  clientId: string;
  lang: string;
  timeZone: string;
  timeZoneOffsetMinutes: number;
  speedUnit: string;
}): JSX.Element => {
  const navigate: NavigateFunction = useNavigate();

  //* Redirect: "/" or "/views/accordion"
  const onRedirectCallback = (appState: AppState | undefined): void => {
    const targetUrl: string = appState?.returnTo || window.location.pathname;
    // console.log("targetUrl:", targetUrl);
    navigate(targetUrl);
  };

  return (
    <React.Fragment>
      <ReactErrorBoundary>
        <I18nextProvider i18n={i18n}>
          <Provider store={store}>
            <Auth0Provider
              domain={domain as string}
              clientId={clientId as string}
              authorizationParams={{
                redirect_uri: window.location.origin,
              }}
              cacheLocation="localstorage"
              onRedirectCallback={onRedirectCallback}
            >
              <AgencyProvider
                url={url as string}
                agencyId={agencyId}
                initialMapRange={initialMapRange}
                agencyName={agencyName}
                initialMapCenter={initialMapCenter}
                lang={lang}
                timeZone={timeZone}
                timeZoneOffsetMinutes={timeZoneOffsetMinutes}
                speedUnit={speedUnit}
              >
                <App />
              </AgencyProvider>
            </Auth0Provider>
          </Provider>
        </I18nextProvider>
      </ReactErrorBoundary>
    </React.Fragment>
  );
};

const Main: React.FC = (): JSX.Element => {
  //* Custom Hook
  const {
    url,
    agencyId,
    agencyName,
    initialMapRange,
    initialMapCenter,
    lang,
    timeZone,
    timeZoneOffsetMinutes,
    speedUnit,
    apiError,
  }: AgencyContextType = useAgencyData() || ({} as AgencyContextType);
  // console.log("apiError:", apiError);

  const { domain, clientId } = getDomainAndClientId(configAgencyName);
  // console.log({ domain, clientId });

  const [showErrorPage, setShowErrorPage] = React.useState<boolean>(false);
  // console.log("showErrorPage:", showErrorPage);

  //* Init: check apiError
  React.useEffect(() => {
    if (apiError && !agencyId) {
      setTimeout(() => {
        setShowErrorPage(true);
      }, 12000);

      return () => setShowErrorPage(false);
    }
  }, [agencyId, apiError]);

  return (
    <React.Fragment>
      {showErrorPage ? (
        <ApiErrorPage />
      ) : !url ||
        !agencyId ||
        !agencyName ||
        !initialMapRange ||
        !initialMapCenter ||
        !domain ||
        !clientId ||
        !lang ||
        !timeZone ||
        !timeZoneOffsetMinutes ||
        !speedUnit ? (
        <TailSpinner height={"100vh"} />
      ) : (
        <Router>
          <MainAppContent
            url={url}
            agencyName={agencyName}
            agencyId={agencyId}
            initialMapCenter={initialMapCenter}
            initialMapRange={initialMapRange}
            domain={domain as string}
            clientId={clientId as string}
            lang={lang}
            timeZone={timeZone}
            timeZoneOffsetMinutes={timeZoneOffsetMinutes}
            speedUnit={speedUnit}
          />
        </Router>
      )}
    </React.Fragment>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <React.StrictMode>
    <Main />
  </React.StrictMode>
);
